import { Table, Column } from "@tanstack/react-table";

import "ui/data-grid/data-grid.types";

export const DRAG_HANDLE_WIDTH_PX = 24;

type UseTableLayoutParams<T> = {
  table: Table<T>;
  pinPrimaryColumnLeft: boolean;
  isDraggable: boolean;
};

export type UseTableLayoutResult<T> = {
  leftColumns: Column<T, unknown>[];
  rightColumns: Column<T, unknown>[];
  renderSpacer: boolean;
  spacerColSpanOffset: number;
  minTableWidth: number;
  getLeftColWidth: (column: Column<T, unknown>) => string;
  getRightColWidth: (column: Column<T, unknown>) => string;
};

export const useTableLayout = <T>({
  table,
  pinPrimaryColumnLeft,
  isDraggable,
}: UseTableLayoutParams<T>): UseTableLayoutResult<T> => {
  const visibleLeafColumns = table.getVisibleLeafColumns();

  const leftColumns = visibleLeafColumns.filter((col) => !col.columnDef.meta?.isPinnedRight);
  const rightColumns = visibleLeafColumns.filter((col) => col.columnDef.meta?.isPinnedRight);

  const renderSpacer = pinPrimaryColumnLeft;
  const spacerColSpanOffset = renderSpacer ? 1 : 0;

  const isFixedWidthColumn = (column: Column<T, unknown>): boolean =>
    !!column.columnDef.meta?.isFixedWidth;

  const flexColumns = leftColumns.filter((col) => !isFixedWidthColumn(col));
  const flexTotalSize = flexColumns.reduce((sum, col) => sum + col.getSize(), 0);

  const leftFixedReservedPx = leftColumns
    .filter(isFixedWidthColumn)
    .reduce((sum, col) => sum + col.getSize(), 0);
  const leftFlexTotalPx = flexTotalSize;
  const rightReservedPx = rightColumns.reduce((sum, col) => sum + col.getSize(), 0);
  const dragHandlePx = isDraggable ? DRAG_HANDLE_WIDTH_PX : 0;
  const reservedPx = leftFixedReservedPx + rightReservedPx + dragHandlePx;
  const minTableWidth = leftFlexTotalPx + leftFixedReservedPx + rightReservedPx + dragHandlePx;

  const getLeftColWidth = (column: Column<T, unknown>): string => {
    if (pinPrimaryColumnLeft || flexTotalSize === 0 || reservedPx === 0) {
      return `${column.getSize()}px`;
    }
    if (isFixedWidthColumn(column)) {
      return `${column.getSize()}px`;
    }
    return `calc((${column.getSize()} / ${flexTotalSize}) * (100% - ${reservedPx}px))`;
  };

  const getRightColWidth = (column: Column<T, unknown>): string => `${column.getSize()}px`;

  return {
    leftColumns,
    rightColumns,
    renderSpacer,
    spacerColSpanOffset,
    minTableWidth,
    getLeftColWidth,
    getRightColWidth,
  };
};
